import { useEffect, useMemo, useState } from 'react';
import {
  Alert,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Stack,
  Typography,
} from '@mui/material';
import ArrowDownwardIcon from '@mui/icons-material/ArrowDownward';
import ArrowUpwardIcon from '@mui/icons-material/ArrowUpward';

import {
  useGetAllUserStandardsQuery,
  useMoveUserStandardMutation,
} from '../../standardsCatalogApi';
import { DestinationPicker } from '../cart/DestinationPicker';

import type { UserStandard, UUID } from '@/shared/types/userStandard';
import { getReorderAnchorPair, type ReorderPlacement } from '@/shared/utils/reorder';

interface MoveToDialogProps {
  open: boolean;
  ownerId: UUID;
  node: UserStandard;
  onClose: () => void;
}

export function MoveToDialog({ open, ownerId, node, onClose }: MoveToDialogProps) {
  const [moveNode, { isLoading }] = useMoveUserStandardMutation();
  const { data: rows = [] } = useGetAllUserStandardsQuery({ ownerId });
  const [destination, setDestination] = useState<UUID | null>(node.parent_id);
  const [error, setError] = useState<string | null>(null);

  // The node's siblings, in display order, so keyboard/touch users can reorder without dragging.
  const siblings = useMemo(
    () =>
      rows
        .filter((row) => (row.parent_id ?? null) === (node.parent_id ?? null))
        .sort(
          (a, b) =>
            a.position - b.position ||
            a.created_at.localeCompare(b.created_at) ||
            a.id.localeCompare(b.id)
        ),
    [rows, node.parent_id]
  );
  const index = siblings.findIndex((s) => s.id === node.id);
  const canMoveUp = index > 0;
  const canMoveDown = index >= 0 && index < siblings.length - 1;

  const isFolder = node.node_type === 'folder';

  useEffect(() => {
    if (open) {
      setDestination(node.parent_id);
      setError(null);
    }
  }, [open, node.parent_id]);

  const handleMove = async () => {
    setError(null);
    try {
      await moveNode({
        id: node.id,
        newParentId: destination,
        ownerId,
        oldParentId: node.parent_id,
      }).unwrap();
      onClose();
    } catch {
      setError('Could not move this item. Please try again.');
    }
  };

  const handleReorder = async (direction: 'up' | 'down') => {
    const anchorId = direction === 'up' ? siblings[index - 1]?.id : siblings[index + 1]?.id;
    if (!anchorId) return;
    const placement: ReorderPlacement = {
      position: direction === 'up' ? 'before' : 'after',
      anchorId,
    };
    const anchors = getReorderAnchorPair(siblings, node.id, placement);
    if (!anchors) return;
    setError(null);
    try {
      await moveNode({
        id: node.id,
        newParentId: node.parent_id,
        beforeId: anchors.beforeId,
        afterId: anchors.afterId,
        ownerId,
        oldParentId: node.parent_id,
      }).unwrap();
    } catch {
      setError('Could not reorder this item. Please try again.');
    }
  };

  return (
    <Dialog open={open} onClose={onClose} fullWidth maxWidth="sm">
      <DialogTitle>Move “{node.code || node.title}”</DialogTitle>
      <DialogContent dividers>
        <Typography variant="body2" color="text.secondary" sx={{ mb: 1.5 }}>
          {isFolder
            ? 'Choose a destination collection, folder, or standard, or move it to the top level to make it its own collection.'
            : 'Choose a destination collection, folder, or standard.'}
        </Typography>
        <DestinationPicker
          ownerId={ownerId}
          value={destination}
          onChange={setDestination}
          excludeId={node.id}
          allowTopLevel={isFolder}
        />
        {siblings.length > 1 && (
          <>
            <Typography variant="subtitle2" sx={{ mt: 2.5, mb: 1 }}>
              Reorder within current folder
            </Typography>
            <Stack direction="row" spacing={1}>
              <Button
                variant="outlined"
                size="small"
                startIcon={<ArrowUpwardIcon />}
                disabled={!canMoveUp || isLoading}
                onClick={() => handleReorder('up')}
              >
                Move up
              </Button>
              <Button
                variant="outlined"
                size="small"
                startIcon={<ArrowDownwardIcon />}
                disabled={!canMoveDown || isLoading}
                onClick={() => handleReorder('down')}
              >
                Move down
              </Button>
            </Stack>
          </>
        )}
        {error && (
          <Alert severity="error" sx={{ mt: 2 }}>
            {error}
          </Alert>
        )}
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose} color="inherit">
          Cancel
        </Button>
        <Button
          variant="contained"
          onClick={handleMove}
          disabled={isLoading || destination === node.parent_id}
        >
          {isLoading ? 'Moving…' : 'Move here'}
        </Button>
      </DialogActions>
    </Dialog>
  );
}

export default MoveToDialog;
